import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  TextField,
  ClickAwayListener
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../../context/PlayerContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import AddBuyInDialog from '../AddBuyInDialog/AddBuyInDialog';
import './PlayerCard.scss';

const PlayerCard = ({ player }) => {
  const navigate = useNavigate();
  const { deletePlayer, editPlayer } = usePlayer();
  const [anchorEl, setAnchorEl] = useState(null);
  const [addBuyInOpen, setAddBuyInOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(player.name);
  const [editError, setEditError] = useState('');

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCardClick = () => {
    if (!isEditing) {
      navigate(`player/${player.id}`);
    }
  };

  const handleDeletePlayer = (event) => {
    event.stopPropagation();
    if (window.confirm(`Are you sure you want to delete ${player.name}?`)) {
      deletePlayer(player.id);
    }
    handleMenuClose();
  };

  const handleAddBuyIn = (event) => {
    event.stopPropagation();
    setAddBuyInOpen(true);
  };

  const handleEditClick = (event) => {
    event.stopPropagation();
    setIsEditing(true);
    setEditedName(player.name);
    setEditError('');
    handleMenuClose();
  };

  const handleSaveEdit = (event) => {
    event.stopPropagation();
    try {
      editPlayer(player.id, editedName);
      setIsEditing(false);
      setEditError('');
    } catch (error) {
      setEditError(error.message);
    }
  };

  const handleCancelEdit = (event) => {
    event.stopPropagation();
    setIsEditing(false);
    setEditedName(player.name);
    setEditError('');
  };

  const handleEditKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSaveEdit(event);
    } else if (event.key === 'Escape') {
      handleCancelEdit(event);
    }
  };

  const handleClickAway = () => {
    if (isEditing) {
      handleCancelEdit({ stopPropagation: () => {} });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getBuyInCount = () => {
    return player.buyInHistory ? player.buyInHistory.length : 0;
  };

  const playerInitials = getInitials(player.name);
  const backgroundColor = player.profileColor || "#006aff";
  const textColor = getContrastTextColor(backgroundColor);

  return (
    <>
      <Card className={`player-card fade-in ${isEditing ? 'editing' : ''}`} onClick={handleCardClick}>
        <CardContent className="player-card-content">
          <Box className="player-card-header">
            <Box className="player-info">
              <Box 
                className="player-avatar"
                style={{
                  backgroundColor: backgroundColor,
                  color: textColor
                }}
              >
                {playerInitials}
              </Box>
              <Box className="player-name-section">
                {isEditing ? (
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <Box className="edit-name-container">
                      <TextField
                        value={editedName}
                        onChange={(e) => {
                          setEditedName(e.target.value);
                          if (editError) setEditError('');
                        }}
                        onKeyDown={handleEditKeyPress}
                        error={!!editError}
                        helperText={editError}
                        size="small"
                        variant="outlined"
                        className="edit-name-input"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Box className="edit-actions">
                        <IconButton
                          size="small"
                          onClick={handleSaveEdit}
                          className="save-button"
                          disabled={!editedName.trim()}
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          className="cancel-button"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </ClickAwayListener>
                ) : (
                  <>
                    <Typography variant="h6" className="player-name">
                      {player.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getBuyInCount()} buy-ins
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
            {!isEditing && (
              <IconButton 
                size="small" 
                onClick={handleMenuOpen}
                className="menu-button"
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
          
          {!isEditing && (
            <>
              <Box className="player-stats">
                <Box className="stat-item">
                  <Typography variant="h4" className="total-amount">
                    {formatCurrency(player.totalBuyIn)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Buy-ins
                  </Typography>
                </Box>
              </Box>

              <Box className="player-actions">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddBuyIn}
                  className="add-buyin-btn"
                  fullWidth
                >
                  Add Buy-in
                </Button>
              </Box>

              {player.totalBuyIn > 0 && (
                <Box className="player-status">
                  <Chip 
                    label="Active Player" 
                    color="success" 
                    size="small"
                    variant="outlined"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem onClick={handleEditClick}>
            <EditIcon sx={{ mr: 1 }} />
            Edit Name
          </MenuItem>
          <MenuItem onClick={handleDeletePlayer} sx={{ color: 'error.main' }}>
            Delete Player
          </MenuItem>
        </Menu>
      </Card>

      <AddBuyInDialog
        open={addBuyInOpen}
        onClose={() => setAddBuyInOpen(false)}
        playerId={player.id}
      />
    </>
  );
};

export default PlayerCard;